import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import ProtectedRoute from './components/ProtectedRoute';
import Navigation from './components/Layout/Navigation';
import ConsentModal from './components/ConsentModal';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PatientDashboard from './pages/PatientDashboard';
import ProviderDashboard from './pages/ProviderDashboard';
import ProviderPatientView from './pages/ProviderPatientView';
import ProviderPatientEdit from './pages/ProviderPatientEdit';
import AdminDashboard from './pages/AdminDashboard';
import CommunityDashboard from './pages/CommunityDashboard';
import AdminPatientEdit from './pages/AdminPatientEdit';
import AccountManagement from './pages/AccountManagement';
import AccountSettings from './pages/AccountSettings';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import EmployerDashboard from './pages/EmployerDashboard';
import MyDocuments from './pages/MyDocuments';
import './App.css';

// Route role constants - extracted to prevent inline array re-creation
const ROLES_PATIENT = ['patient'];
const ROLES_PROVIDER = ['provider'];
const ROLES_ADMIN = ['admin'];
const ROLES_EMPLOYER = ['employer'];
const ROLES_ALL = ['patient', 'provider', 'admin', 'employer'];
const ROLES_PATIENT_PROVIDER = ['patient', 'provider'];

const RootRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-700"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  switch (user.role) {
    case 'patient':
      return <Navigate to="/dashboard" replace />;
    case 'provider':
      return <Navigate to="/provider/patients" replace />;
    case 'admin':
      return <Navigate to="/admin/dashboard" replace />;
    case 'employer':
      return <Navigate to="/employer/dashboard" replace />;
    default:
      return <Navigate to="/login" replace />;
  }
};

const Unauthorized = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="text-center">
      <h1 className="text-4xl font-bold text-slate-900 mb-4">403 - Unauthorized</h1>
      <p className="text-slate-600 mb-6">You don't have permission to access this page.</p>
      <a href="/" className="btn-primary inline-block px-6 py-2.5 rounded-full">
        Go to Dashboard
      </a>
    </div>
  </div>
);

const AppContent = () => {
  const { user, updateUser } = useAuth();
  const [showConsentModal, setShowConsentModal] = useState(false);

  useEffect(() => {
    // Show consent modal if user is logged in and hasn't accepted terms
    // Also check localStorage to avoid showing repeatedly during session issues
    const sessionConsentDismissed = sessionStorage.getItem('consent_dismissed');
    
    if (user && !user.terms_accepted && !sessionConsentDismissed) {
      setShowConsentModal(true);
    } else {
      setShowConsentModal(false);
    }
  }, [user]);

  const handleConsentAccept = () => {
    setShowConsentModal(false);
    // Update user context to reflect terms accepted
    if (user) {
      updateUser({ terms_accepted: true });
    }
    // Clear the session flag since they've accepted
    sessionStorage.removeItem('consent_dismissed');
  };

  const handleConsentDismiss = () => {
    setShowConsentModal(false);
    // Store in sessionStorage so modal doesn't show again this session
    // This prevents annoying repeated showing on session issues
    sessionStorage.setItem('consent_dismissed', 'true');
  };

  return (
    <div className="App">
      {user && <Navigation />}
      
      {/* Consent Modal - Shows on first login */}
      {user && (
        <ConsentModal 
          open={showConsentModal} 
          onAccept={handleConsentAccept}
          onDismiss={handleConsentDismiss}
        />
      )}
      
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/unauthorized" element={<Unauthorized />} />

        <Route
          path="/dashboard"
          element={
            <ProtectedRoute allowedRoles={ROLES_PATIENT}>
              <PatientDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/provider/patients"
          element={
            <ProtectedRoute allowedRoles={ROLES_PROVIDER}>
              <ProviderDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/provider/patient/:patientId/edit"
          element={
            <ProtectedRoute allowedRoles={ROLES_PROVIDER}>
              <ProviderPatientEdit />
            </ProtectedRoute>
          }
        />

        <Route
          path="/provider/patient/:patientId/view"
          element={
            <ProtectedRoute allowedRoles={ROLES_PROVIDER}>
              <ProviderPatientView />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute allowedRoles={ROLES_ADMIN}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/accounts"
          element={
            <ProtectedRoute allowedRoles={ROLES_ADMIN}>
              <AccountManagement />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/patient/:patientId/edit"
          element={
            <ProtectedRoute allowedRoles={ROLES_ADMIN}>
              <AdminPatientEdit />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings"
          element={
            <ProtectedRoute allowedRoles={ROLES_ALL}>
              <AccountSettings />
            </ProtectedRoute>
          }
        />

        <Route
          path="/community"
          element={
            <ProtectedRoute allowedRoles={ROLES_ALL}>
              <CommunityDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer/dashboard"
          element={
            <ProtectedRoute allowedRoles={ROLES_EMPLOYER}>
              <EmployerDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/documents"
          element={
            <ProtectedRoute allowedRoles={ROLES_PATIENT_PROVIDER}>
              <MyDocuments />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;